// Task-list export helpers — PDF, CSV, Excel. Operates on the rows passed in
// (the project page hands over its currently filtered tasks). Mirrors
// timesheetExport.js.
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { TASK_STATUS, PRIORITY, localDateStr, fmtDuration } from "../wsConstants";

const COLUMNS = [
  { key: "title", label: "Task" },
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
  { key: "due_date", label: "Due date" },
  { key: "assignees", label: "Assignees" },
  { key: "collaborators", label: "Collaborators" },
  { key: "watchers", label: "Watchers" },
  { key: "logged", label: "Time logged" },
  { key: "hours", label: "Hours" },
  { key: "comments", label: "Comments" },
  { key: "created", label: "Created" },
  { key: "description", label: "Description" },
];
// The PDF page is too narrow for every column; drop the ones that only make
// sense in a spreadsheet (exact hours, long description).
const PDF_COLUMNS = COLUMNS.filter((c) => !["hours", "description"].includes(c.key));

const names = (t, bucket) =>
  (t.members || [])
    .filter((m) => (m.member_role || "assignee") === bucket)
    .map((m) => m.full_name?.trim() || m.username || `User #${m.user_id}`)
    .join(", ");

// Flat, display-ready fields for one task.
const toRow = (t) => {
  const mins = Number(t.logged_minutes) || 0;
  return {
    title: t.title || "",
    status: TASK_STATUS[t.status]?.label || t.status || "",
    priority: PRIORITY[t.priority]?.label || t.priority || "",
    due_date: t.due_date ? String(t.due_date).slice(0, 10) : "",
    assignees: names(t, "assignee"),
    collaborators: names(t, "collaborator"),
    watchers: names(t, "watcher"),
    logged: mins ? fmtDuration(mins) : "",
    hours: Math.round((mins / 60) * 100) / 100,
    comments: Number(t.comment_count) || 0,
    created: t.created_at ? String(t.created_at).slice(0, 10) : "",
    description: t.description || "",
  };
};

// "AireXpert" → "airexpert-tasks-2026-09-28"
const fileBase = (projectName) => {
  const slug = String(projectName || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "project";
  // Local day — toISOString is UTC and would stamp a late export with tomorrow.
  return `${slug}-tasks-${localDateStr()}`;
};

export function downloadTasksCSV(tasks, meta = {}) {
  const rows = tasks.map(toRow);
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = COLUMNS.map((c) => c.label).join(",");
  const body = rows.map((r) => COLUMNS.map((c) => esc(r[c.key])).join(",")).join("\n");
  // BOM so Excel opens non-ASCII names correctly when the CSV is double-clicked.
  const blob = new Blob([`﻿${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `${fileBase(meta.projectName)}.csv`);
}

export function downloadTasksExcel(tasks, meta = {}) {
  const rows = tasks.map(toRow);
  const aoa = [COLUMNS.map((c) => c.label), ...rows.map((r) => COLUMNS.map((c) => r[c.key]))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = COLUMNS.map((c) => ({ wch: c.key === "description" ? 50 : c.key === "title" ? 36 : 16 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Tasks");
  XLSX.writeFile(wb, `${fileBase(meta.projectName)}.xlsx`);
}

export function downloadTasksPDF(tasks, meta = {}) {
  const rows = tasks.map(toRow);
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(14);
  doc.text(`${meta.projectName || "Project"} — Tasks`, 14, 16);
  doc.setFontSize(9);
  const done = tasks.filter((t) => t.status === "done").length;
  const totalMins = tasks.reduce((s, t) => s + (Number(t.logged_minutes) || 0), 0);
  const sub = [
    `${tasks.length} task${tasks.length === 1 ? "" : "s"}`,
    `${done} done`,
    totalMins ? `${fmtDuration(totalMins)} logged` : null,
    meta.filters,
    `Exported ${localDateStr()}`,
  ]
    .filter(Boolean)
    .join("  ·  ");
  doc.text(sub, 14, 22);
  autoTable(doc, {
    startY: 26,
    head: [PDF_COLUMNS.map((c) => c.label)],
    body: rows.map((r) => PDF_COLUMNS.map((c) => r[c.key])),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [245, 75, 1] },
    columnStyles: { 0: { cellWidth: 60 } },
  });
  doc.save(`${fileBase(meta.projectName)}.pdf`);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
